import { useEffect, useState } from 'react';
import { getAuthHeaders } from '@/utils/api';

/**
 * Fetches an authenticated file (PDF/image) from the API and exposes it as a
 * same-origin blob URL.
 *
 * Why: uploaded documents are served from the API origin (api.nexedu.ma). The
 * API sends X-Frame-Options/CSP, so embedding those URLs directly in an <iframe>
 * (or even <img> when the endpoint needs auth) fails cross-origin
 * ("refused to connect"). A blob URL is same-origin to the page, so framing
 * rules don't apply and the Authorization header is sent properly.
 *
 * Returns a blob URL that is automatically revoked on unmount / url change.
 */
export function useAuthedFileBlob(
  url: string | null | undefined,
  active: boolean = true,
): { blobUrl: string | null; loading: boolean; error: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!active || !url) {
      setBlobUrl(null);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);
    setBlobUrl(null);

    const headers = getAuthHeaders();
    delete headers['Content-Type']; // let the browser handle the response type

    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, active]);

  return { blobUrl, loading, error };
}
